import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, ChevronDown, LogOut, Menu, Moon, Plus, Sun, User } from "lucide-react";

import { Modal } from "./Modal";
import { useAuth } from "./AuthProvider";
import { useTheme } from "./ThemeProvider";
import { cn } from "../utils/cn";

interface TopbarProps {
  onMenuClick?: () => void;
  collapsed?: boolean;
  appScope?: "business" | "platform-admin";
}

/**
 * Hook that safely fetches notifications only when a Redux store is available (business app).
 * Returns { hasUnread } — always false for platform-admin since it has no Redux Provider.
 */
function useNotificationBadge(isPlatformAdmin: boolean, user: any): { hasUnread: boolean } {
  const [hasUnread, setHasUnread] = useState(false);

  useEffect(() => {
    if (isPlatformAdmin || !user) return;

    // Dynamically import to avoid pulling Redux context in platform-admin
    let cancelled = false;
    const fetchNotifications = async () => {
      try {
        const { api } = await import("../utils/api-client");
        const result = await api.get<any[]>("/api/business/notifications");
        if (!cancelled && Array.isArray(result)) {
          setHasUnread(result.some((n: any) => !n.read));
        }
      } catch { /* silent */ }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isPlatformAdmin, user]);

  return { hasUnread };
}

export const Topbar = ({ onMenuClick, collapsed, appScope = "business" }: TopbarProps) => {
  const navigate = useNavigate();
  const { resolveRoleLabel, user, canAccessRoute, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const isPlatformAdmin = appScope === "platform-admin";
  const profileHref = isPlatformAdmin ? "/console/profile" : "/profile";

  // Only fetch notifications for business app (platform-admin has no Redux store)
  const { hasUnread } = useNotificationBadge(isPlatformAdmin, user);

  const quickActions = [
    { label: "Add Vehicle", href: "/vehicles", description: "Open the vehicle management screen." },
    { label: "Add Customer", href: "/customers", description: "Go to customer management." },
    { label: "Create Booking", href: "/bookings", description: "Jump to booking creation." },
  ].filter((item) => canAccessRoute(item.href));

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!profileMenuRef.current) return;
      if (!profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <header data-topbar className={cn(
        "fixed left-0 right-0 top-0 z-10 h-16 border-b border-border bg-background/80 backdrop-blur-md transition-[left] duration-200",
        collapsed ? "lg:left-24" : "lg:left-72"
      )}>
        <div className="flex h-full items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={onMenuClick}
            className="flex h-11 w-11 items-center justify-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground lg:hidden"
            aria-label="Open navigation menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex-1" />

          <div className="ml-2 flex items-center gap-2 sm:ml-4 sm:gap-4">
            {quickActions.length > 0 ? (
              <button
                type="button"
                onClick={() => setShowQuickAdd(true)}
                className="hidden cursor-pointer items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/20 transition-all hover:bg-primary-hover sm:flex"
              >
                <Plus className="h-4 w-4" />
                <span>Quick Add</span>
              </button>
            ) : null}

            <div className="mx-1 hidden h-6 w-px bg-border sm:block" />

            <button
              type="button"
              onClick={() => navigate(isPlatformAdmin ? "/console/notifications" : "/notifications")}
              className="relative flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground transition-all hover:bg-muted hover:text-foreground"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5" />
              {hasUnread && (
                <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-destructive ring-2 ring-background" aria-hidden="true" />
              )}
            </button>

            {/* Commented by the owner — no need for dark mode as of now */}
            {/* <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
              className="flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground transition-all hover:bg-muted hover:text-foreground"
            >
              {theme === "light" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </button> */}

            <div ref={profileMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setShowProfileMenu((current) => !current)}
                className="flex items-center gap-2 rounded-full border border-border bg-muted/40 p-1.5 pl-2 pr-3 transition-all hover:bg-muted"
              >
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md shadow-primary/20 overflow-hidden">
                  {(user as any)?.avatarUrl ? (
                    <img src={(user as any).avatarUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                </div>
                <span className="hidden sm:block text-left">
                  <span className="block text-sm font-semibold text-foreground">{user?.name ?? "User"}</span>
                  <span className="block text-[11px] text-muted-foreground">
                    {user ? resolveRoleLabel(user) : "Role"}
                  </span>
                </span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showProfileMenu ? "rotate-180" : ""}`} />
              </button>

              {showProfileMenu ? (
                <div className="absolute right-0 mt-2 w-52 rounded-2xl border border-border bg-card p-2 shadow-main">
                  <Link to={profileHref}
                    onClick={() => setShowProfileMenu(false)}
                    className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-muted min-h-[44px]"
                  >
                    <User className="h-4 w-4 text-muted-foreground" />
                    Profile
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setShowProfileMenu(false);
                      signOut();
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-destructive transition-colors hover:bg-destructive/10 min-h-[44px]"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <Modal isOpen={showQuickAdd} onClose={() => setShowQuickAdd(false)} title="Quick Add">
        <div className="space-y-3">
          {quickActions.length === 0 ? (
            <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
              No quick actions are available for your role.
            </div>
          ) : (
            quickActions.map((action) => (
              <button
                key={action.href}
                type="button"
                onClick={() => {
                  setShowQuickAdd(false);
                  navigate(action.href);
                }}
                className="w-full rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-muted min-h-[44px]"
              >
                <div className="text-sm font-semibold text-foreground">{action.label}</div>
                <div className="mt-1 text-xs text-muted-foreground">{action.description}</div>
              </button>
            ))
          )}
        </div>
      </Modal>
    </>
  );
};

export default Topbar;
